/* bm_peakExplorer_variants.js – Variant-Bridge mit Größen-Parsing */
(function () {
  window.BM = window.BM || {};
  if (window.BM.__peakExplorerVariantsLoaded) return;
  window.BM.__peakExplorerVariantsLoaded = true;

  const U = window.BM.util || null;
  const FORM_SELECTOR = 'form[action*="/cart/add"], form.product-form, [data-product-form]';
  const PRESSED_SELECTOR = '[aria-pressed="true"], [aria-checked="true"], .is-selected, [data-selected="true"], .active, [data-state="on"]';

  function parseScaleFromString(input) {
    if (!input) return null;
    const match = String(input).match(/1\s*:\s*([\d.,\s]+)/);
    if (!match) return null;

    const normalized = match[1].trim().replace(/[.\s]/g, '').replace(',', '.');
    const scale = Number(normalized);
    return Number.isFinite(scale) && scale > 0 ? scale : null;
  }

  function getProductJSON(rootEl) {
    const productEl = rootEl?.querySelector('script[type="application/json"][data-product]');
    if (!productEl) return null;

    try {
      return JSON.parse(productEl.textContent || '{}');
    } catch {
      return null;
    }
  }

  function findVariantById(productJson, idLike) {
    if (!productJson?.variants?.length) return null;
    const targetId = Number(idLike);
    if (!Number.isFinite(targetId)) return null;
    return productJson.variants.find(variant => Number(variant.id) === targetId) || null;
  }

  function initVariantBridge(rootEl, applyPrintSize, applyScaleDenom, applyColorHex) {
    if (!U || !rootEl) return null;

    const doc = rootEl.ownerDocument || document;
    const cleanupFns = [];
    const productJson = getProductJSON(rootEl);
    const productVariantIds = new Set(
      (productJson?.variants || [])
        .map(variant => Number(variant?.id))
        .filter(Number.isFinite)
    );
    const handledLegacyEvents = new WeakSet();

    let initialSyncTimeoutId = null;
    let last = {
      sizeKey: '',
      scale: null,
      color: '',
      varId: null
    };

    function toSizeKey(sizeInfo) {
      if (!sizeInfo) return '';
      return `${sizeInfo.printSizeCm}|${sizeInfo.displayLabel}`;
    }

    function applyFromSize(sizeInfo) {
      const normalized = U.sizeInfoFromUnknown(sizeInfo);
      if (!normalized) return;

      const nextKey = toSizeKey(normalized);
      if (nextKey === last.sizeKey) return;

      last.sizeKey = nextKey;
      applyPrintSize?.(normalized, true);
    }

    function applyFromScale(denom) {
      const scale = Number(denom);
      if (!Number.isFinite(scale) || scale <= 0) return;
      if (U.nearlyEqual(last.scale, scale, 1e-9)) return;

      last.scale = scale;
      applyScaleDenom?.(scale, true);
    }

    function applyFromColor(hex) {
      const color = typeof hex === 'string' ? hex.trim() : '';
      if (!color || color === last.color) return;

      last.color = color;
      applyColorHex?.(color);
    }

    function onLegacy(event) {
      if (event && typeof event === 'object') {
        if (handledLegacyEvents.has(event)) return;
        handledLegacyEvents.add(event);
      }

      const detail = event?.detail || {};
      if (detail.sizeMM != null) {
        applyFromSize(U.sizeInfoFromCm(Number(detail.sizeMM) / 10, {
          unit: 'mm',
          source: 'legacy:sizeMM'
        }));
      }
      if (detail.scaleFactor != null) applyFromScale(detail.scaleFactor);
      if (detail.colorHex) applyFromColor(detail.colorHex);
    }

    window.addEventListener('productVariantChanged', onLegacy);
    doc.addEventListener('productVariantChanged', onLegacy);
    cleanupFns.push(() => window.removeEventListener('productVariantChanged', onLegacy));
    cleanupFns.push(() => doc.removeEventListener('productVariantChanged', onLegacy));

    function onVariantEvent(event) {
      const detail = event?.detail || {};
      const sizeInfo =
        U.sizeInfoFromDetail(detail) ||
        U.sizeInfoFromVariant(detail.variant) ||
        U.sizeInfoFromVariant(detail);

      if (sizeInfo) {
        applyFromSize(sizeInfo);
        return;
      }

      applySniffedSelection();
    }

    ['variant:change', 'variant:changed', 'product:variant-change', 'theme:variant:change'].forEach(eventName => {
      doc.addEventListener(eventName, onVariantEvent);
      cleanupFns.push(() => doc.removeEventListener(eventName, onVariantEvent));
    });

    function isRelevantForm(form) {
      if (!form?.matches?.(FORM_SELECTOR)) return false;
      if (!productVariantIds.size) return true;

      const idControl = form.querySelector?.('[name="id"]');
      const variantId = Number(idControl?.value);
      return Number.isFinite(variantId) && productVariantIds.has(variantId);
    }

    function findRelevantForm() {
      const forms = Array.from(doc.querySelectorAll(FORM_SELECTOR));
      return forms.find(isRelevantForm) || (!productVariantIds.size ? forms[0] : null) || null;
    }

    function sniffSizeFromForm() {
      const form = findRelevantForm();
      if (!form) return null;

      const idInput = form.querySelector('[name="id"]');
      if (idInput?.value && productJson?.variants?.length) {
        const varId = String(idInput.value);
        if (varId !== last.varId) {
          last.varId = varId;
          const variant = findVariantById(productJson, varId);
          if (variant) {
            const sizeInfo = U.sizeInfoFromVariant(variant);
            if (sizeInfo) return { sizeInfo };

            const scale = parseScaleFromString(variant.title)
              || parseScaleFromString(`${variant.option1 || ''} ${variant.option2 || ''} ${variant.option3 || ''}`)
              || Number(variant.metafields?.scale)
              || Number(variant.metafields?.scale_denom);

            if (Number.isFinite(scale) && scale > 0) return { scale };
          }
        }
      }

      const radios = form.querySelectorAll('input[type="radio"][name^="options"], input[type="radio"][name*="option"]');
      for (const radio of radios) {
        if (!radio.checked) continue;

        let labelText = '';
        if (radio.nextElementSibling?.tagName === 'LABEL') {
          labelText = radio.nextElementSibling.textContent || '';
        } else if (radio.id) {
          const label = form.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
          if (label) labelText = label.textContent || '';
        }

        const sizeInfo = U.parseDimensionsFromString(labelText);
        if (sizeInfo) return { sizeInfo };
      }

      const pressed = form.querySelectorAll(PRESSED_SELECTOR);
      for (const element of pressed) {
        const text = element.textContent?.trim() || element.getAttribute('aria-label') || '';
        const sizeInfo = U.parseDimensionsFromString(text);
        if (sizeInfo) return { sizeInfo };
      }

      const selects = form.querySelectorAll('select[name^="options"], select[name*="option"]');
      for (const select of selects) {
        const option = select.options?.[select.selectedIndex];
        const text = (option?.text || select.value || '').trim();
        const sizeInfo = U.parseDimensionsFromString(text);
        if (sizeInfo) return { sizeInfo };
      }

      return null;
    }

    function applySniffedSelection() {
      const sniffed = sniffSizeFromForm();
      if (sniffed?.sizeInfo) {
        applyFromSize(sniffed.sizeInfo);
      } else if (sniffed?.scale != null) {
        applyFromScale(sniffed.scale);
      }
    }

    const onFormChange = event => {
      const element = event.target;
      const form = element?.closest?.(FORM_SELECTOR);
      if (!isRelevantForm(form)) return;

      const name = element?.name || '';
      if (!(name === 'id' || name.startsWith('options') || name.includes('option'))) return;
      applySniffedSelection();
    };

    doc.addEventListener('change', onFormChange, true);
    cleanupFns.push(() => doc.removeEventListener('change', onFormChange, true));

    const initialForm = findRelevantForm();
    if (initialForm && typeof MutationObserver === 'function') {
      const onObservedVariantChange = U.rafThrottle(applySniffedSelection);
      const observer = new MutationObserver(onObservedVariantChange);
      observer.observe(initialForm, {
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-pressed', 'aria-checked', 'data-selected', 'data-state']
      });

      cleanupFns.push(() => {
        observer.disconnect();
        onObservedVariantChange.cancel?.();
      });
    }

    initialSyncTimeoutId = window.setTimeout(() => {
      initialSyncTimeoutId = null;
      applySniffedSelection();
    }, 0);

    return function cleanupVariantBridge() {
      if (initialSyncTimeoutId) {
        window.clearTimeout(initialSyncTimeoutId);
        initialSyncTimeoutId = null;
      }

      cleanupFns.forEach(fn => fn());
      cleanupFns.length = 0;
      last = null;
    };
  }

  window.BM.initVariantBridge = initVariantBridge;
})();
